import { useEffect, useReducer, useCallback, useState } from "react";
import { useIntl, defineMessages } from "react-intl";
import { waitForPreloads } from "../../utils/preload";
import qsTruthy from "../../utils/qs_truthy";

function reducer(state, action) {
  switch (action.type) {
    case "done-preloading":
      return { ...state, donePreloading: true };
    case "object-loading":
      return { ...state, objectCount: state.objectCount + 1 };
    case "object-loaded":
      return { ...state, loadedCount: state.loadedCount + 1 };
    case "all-objects-loaded":
      return {
        ...state,
        allObjectsLoaded: true
      };
    case "environment-loaded": {
      return {
        ...state,
        environmentLoaded: true
      };
    }
    case "network-connected":
      return {
        ...state,
        networkConnected: true
      };
    case "dialog-connected":
      return {
        ...state,
        dialogConnected: true
      };
    case "scene-reset":
      return {
        ...state,
        objectCount: 0,
        loadedCount: 0,
        allObjectsLoaded: false,
        environmentLoaded: false,
        networkConnected: false,
        dialogConnected: false,
        donePreloading: false
      };
  }
}

// Check if the current page is loading an external asset
function hasExternalAssetParams() {
  const urlParams = new URLSearchParams(window.location.search);
  const fileId = urlParams.get("fileId") || urlParams.get("assetId");
  const glbUrl = urlParams.get("myMediaModel");
  return !!(fileId || glbUrl);
}

// defineMessages informs babel-plugin-react-intl what i18n data can be stripped/embedded when bundling.
const messages = defineMessages({
  default: {
    id: "loading-screen.default",
    description: "The scene has started loading.",
    defaultMessage: "Loading Verse..."
  },
  loadingObjects: {
    id: "loading-screen.loading-objects",
    description: "The loading progress. How many objects have finished loading?",
    defaultMessage: "Loading objects {loadedCount}/{objectCount}"
  },
  connectingScene: {
    id: "loading-screen.connecting",
    description: "The scene is loaded, we are waiting for the networked scene to be connected to enter.",
    defaultMessage: "Connecting to the Verse..."
  },
  enteringRoom: {
    id: "loading-screen.entering-room",
    description:
      "Once the scene has finished loading, this message tells users that they will be entering the Verse shortly.",
    defaultMessage: "Entering Verse..."
  }
});

const newLoader = qsTruthy("newLoader");
export function useRoomLoadingState(sceneEl) {
  const [isLazyLoadingStrategy, setIsLazyLoadingStrategy] = useState(false);

  // External asset loading state
  const [externalAssetLoading, setExternalAssetLoading] = useState(false);
  const [externalAssetLoaded, setExternalAssetLoaded] = useState(false);
  const [externalAssetError, setExternalAssetError] = useState(null);

  const [
    { environmentLoaded, networkConnected, allObjectsLoaded, donePreloading, objectCount, loadedCount },
    dispatch
  ] = useReducer(reducer, {
    objectCount: 0,
    loadedCount: 0,
    allObjectsLoaded: false,
    environmentLoaded: false,
    networkConnected: false,
    dialogConnected: false,
    donePreloading: false
  });
  // Skip object loading callbacks for the newLoader, since they don't yet fire events we can listen to.
  const doneLoadingObjects = newLoader || isLazyLoadingStrategy || allObjectsLoaded;

  // Include external asset loading in the done condition
  const doneLoadingExternalAsset = !externalAssetLoading || externalAssetLoaded || externalAssetError;
  const done =
    sceneEl.is("loaded") ||
    (environmentLoaded && networkConnected && doneLoadingObjects && donePreloading && doneLoadingExternalAsset);

  let messageKey = "default";
  if (!environmentLoaded) {
    messageKey = "default";
  } else if (!networkConnected) {
    messageKey = "connectingScene";
  } else if (!doneLoadingObjects) {
    messageKey = "loadingObjects";
  } else {
    messageKey = "enteringRoom";
  }

  const onObjectLoading = useCallback(() => {
    dispatch({ type: "object-loading" });
  }, [dispatch]);

  const onObjectLoaded = useCallback(() => {
    dispatch({ type: "object-loaded" });
  }, [dispatch]);

  useEffect(() => {
    const handleAllObjectsLoaded = () => {
      dispatch({ type: "all-objects-loaded" });
    };

    sceneEl.addEventListener("all-objects-loaded", handleAllObjectsLoaded);
    return () => {
      sceneEl.removeEventListener("all-objects-loaded", handleAllObjectsLoaded);
    };
  }, [sceneEl, dispatch]);

  const onEnvironmentLoaded = useCallback(() => {
    dispatch({ type: "environment-loaded" });

    // Read loading strategy
    const strategyAttr = document.querySelector("[loading-strategy]")?.getAttribute("loading-strategy");
    const strategy = strategyAttr ? strategyAttr.strategy : "lazy";
    setIsLazyLoadingStrategy(strategy === "lazy");
  }, [dispatch]);

  const onNetworkConnected = useCallback(() => {
    dispatch({ type: "network-connected" });
  }, [dispatch]);

  const onDialogConnected = useCallback(() => {
    dispatch({ type: "dialog-connected" });
  }, [dispatch]);

  // Handle external asset loading
  useEffect(() => {
    if (hasExternalAssetParams()) {
      setExternalAssetLoading(true);
      let inspectDelayTimeout;

      const handleMediaLoaded = e => {
        if (e.detail || e.target) {
          // Add delay to ensure inspect mode has time to engage
          // external-glb-importer has a 500ms delay before calling inspect,
          // so we wait 1000ms total to be safe
          inspectDelayTimeout = setTimeout(() => {
            setExternalAssetLoaded(true);
          }, 1000);
        }
      };

      const handleMediaError = e => {
        console.error("External asset failed to load:", e);
        setExternalAssetError(e.detail || e);
      };

      // Listen for media-loaded and unified media-error events
      sceneEl.addEventListener("media-loaded", handleMediaLoaded, { once: true });
      sceneEl.addEventListener("media-error", handleMediaError, { once: true });

      return () => {
        sceneEl.removeEventListener("media-loaded", handleMediaLoaded);
        sceneEl.removeEventListener("media-error", handleMediaError);
        if (inspectDelayTimeout) {
          clearTimeout(inspectDelayTimeout);
        }
      };
    }
  }, [sceneEl]);

  useEffect(() => {
    waitForPreloads().then(() => {
      // TODO: Is this OK to do? Seems bad to be async here somehow
      dispatch({ type: "done-preloading" });
    });
    return () => {};
  }, []);

  useEffect(() => {
    // Once the scene has loaded the dependencies to this hook will change,
    // the event listeners will be removed, and we can prevent adding them again.
    if (!done) {
      // Always listen to object loading/loaded events (even in lazy mode for tracking)
      sceneEl.addEventListener("model-loading", onObjectLoading);
      sceneEl.addEventListener("image-loading", onObjectLoading);
      sceneEl.addEventListener("pdf-loading", onObjectLoading);
      sceneEl.addEventListener("video-loading", onObjectLoading);
      sceneEl.addEventListener("model-loaded", onObjectLoaded);
      sceneEl.addEventListener("image-loaded", onObjectLoaded);
      sceneEl.addEventListener("pdf-loaded", onObjectLoaded);
      sceneEl.addEventListener("video-loaded", onObjectLoaded);
      sceneEl.addEventListener("model-error", onObjectLoaded);
      sceneEl.addEventListener("environment-scene-loaded", onEnvironmentLoaded);

      // Reset loading state when scene changes
      const onSceneReset = () => {
        dispatch({ type: "scene-reset" });
        setIsLazyLoadingStrategy(false);
      };
      sceneEl.addEventListener("reset_scene", onSceneReset);
      sceneEl.addEventListener("didConnectToNetworkedScene", onNetworkConnected);
      sceneEl.addEventListener("didConnectToDialog", onDialogConnected);
    }

    return () => {
      sceneEl.removeEventListener("model-loading", onObjectLoading);
      sceneEl.removeEventListener("image-loading", onObjectLoading);
      sceneEl.removeEventListener("pdf-loading", onObjectLoading);
      sceneEl.removeEventListener("video-loading", onObjectLoading);
      sceneEl.removeEventListener("model-loaded", onObjectLoaded);
      sceneEl.removeEventListener("image-loaded", onObjectLoaded);
      sceneEl.removeEventListener("pdf-loaded", onObjectLoaded);
      sceneEl.removeEventListener("video-loaded", onObjectLoaded);
      sceneEl.removeEventListener("model-error", onObjectLoaded);
      sceneEl.removeEventListener("environment-scene-loaded", onEnvironmentLoaded);
      sceneEl.removeEventListener("didConnectToNetworkedScene", onNetworkConnected);
      sceneEl.removeEventListener("didConnectToDialog", onDialogConnected);
    };
  }, [sceneEl, done, onObjectLoaded, onObjectLoading, onEnvironmentLoaded, onNetworkConnected, onDialogConnected]);

  const intl = useIntl();

  const message = intl.formatMessage(messages[messageKey], {
    // Never show a loaded count that's greater than the object count
    loadedCount: Math.min(loadedCount, objectCount),
    objectCount
  });

  useEffect(() => {
    if (done) {
      sceneEl.addState("loaded");
    } else {
      sceneEl.removeState("loaded");
    }
  }, [sceneEl, done]);

  // Adjust counts to include external asset
  const totalObjectCount = externalAssetLoading ? objectCount + 1 : objectCount;
  const totalLoadedCount = externalAssetLoaded ? loadedCount + 1 : loadedCount;

  return {
    loading: !done,
    message,
    objectCount: totalObjectCount,
    loadedCount: totalLoadedCount,
    environmentLoaded,
    donePreloading,
    allObjectsLoaded,
    externalAssetLoading,
    externalAssetLoaded,
    externalAssetError
  };
}
